import { IntentId, NlpIntent, NlpModelId, StepId } from '@feedyou/schema'
import { AnalysisWarning } from '@feedyou/utils'
import _ from 'lodash'
import { createSelector } from 'reselect'
import { STABLE_EMPTY_ARRAY } from '@feedyou/utils'

import { RootState } from '../../reducers'
import { nlpModelByIdSelector, stepNlpModelSelector } from '../botNlp/selectors'
import { NlpAnalyzerState } from './nlpAnalyzerSlice'

import { isIntent } from '../../features/nlpIntentList'
import { stepSelector } from '../flow/selectors/selectors'

export const nlpAnalysisSliceSelector = (state: RootState): NlpAnalyzerState => state.nlpAnalyzer

export const isAnalysisUpToDateSelector = (state: RootState) =>
  nlpAnalysisSliceSelector(state).isAnalysisUpToDate

export const isAnalysisFetchingSelector = (state: RootState) =>
  nlpAnalysisSliceSelector(state).isFetching

export const isAnalysisBannerClosed = (state: RootState) =>
  nlpAnalysisSliceSelector(state).isBannerClosed

const analysisStatsSelector = (state: RootState) =>
  nlpAnalysisSliceSelector(state).analysisResult?.stats

export const analysisWarningsSelector = (state: RootState) =>
  nlpAnalysisSliceSelector(state).analysisResult?.analysisWarnings ?? STABLE_EMPTY_ARRAY

export const modelStatsForIntentSelector = _.memoize((intentId: IntentId) =>
  createSelector(
    analysisStatsSelector, // I <3 PRETTIER
    stats => stats?.find(stat => stat.intentId === intentId)
  )
)

export const analysisWarningsForIntentIdSelector = _.memoize((intentId: IntentId) =>
  createSelector(
    analysisWarningsSelector, // I <3 PRETTIER
    (warnings): AnalysisWarning[] => {
      return warnings.filter(warning => warning.usedInIntents.includes(intentId))
    }
  )
)

export const analysisWarningsForNlpModelSelector =
  (modelId: NlpModelId) =>
  (state: RootState): AnalysisWarning[] | undefined => {
    const model = nlpModelByIdSelector(modelId)(state)
    const intents: NlpIntent[] = model?.intents?.filter(isIntent) ?? STABLE_EMPTY_ARRAY

    return intents?.flatMap(
      intent => analysisWarningsForIntentIdSelector(intent.id)(state) ?? STABLE_EMPTY_ARRAY
    )
  }

export const analysisWarningsForStepNlpModelSelector =
  (stepId: StepId) =>
  (state: RootState): AnalysisWarning[] | undefined => {
    const step = stepSelector(stepId)(state)

    if (!step) {
      return []
    }

    const nlpModel = stepNlpModelSelector(stepId)(state)

    if (!nlpModel?.intents) {
      return []
    }

    return nlpModel.intents.flatMap(intent => analysisWarningsForIntentIdSelector(intent.id)(state))
  }
